/**
 * useWaveform - Generates audio waveform data using Web Audio API
 * Caches waveform data to avoid regenerating
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getAssetUrl } from '@/utils/studioUtils';

// Global cache for waveform data
const waveformCache = new Map();

/**
 * Generate waveform data from audio buffer
 * @param {AudioBuffer} audioBuffer 
 * @param {number} samples - Number of samples to generate
 * @returns {number[]} - Array of amplitude values (0-1)
 */
function generateWaveformFromBuffer(audioBuffer, samples = 100) {
  const channelData = audioBuffer.getChannelData(0); // Use first channel
  const blockSize = Math.floor(channelData.length / samples);
  const waveform = [];
  
  for (let i = 0; i < samples; i++) {
    const start = i * blockSize;
    let sum = 0;
    let max = 0;
    
    for (let j = 0; j < blockSize; j++) {
      const value = Math.abs(channelData[start + j] || 0);
      sum += value;
      if (value > max) max = value;
    }
    
    // Use RMS (root mean square) for smoother visualization
    const rms = Math.sqrt(sum / blockSize);
    // Normalize and boost low values for better visibility
    const normalized = Math.min(1, rms * 2);
    waveform.push(normalized);
  }
  
  return waveform;
}

/**
 * Hook to generate and cache waveform data for an audio URL
 */
export function useWaveform(audioUrl, samples = 100) {
  const [waveformData, setWaveformData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const audioContextRef = useRef(null);

  const generateWaveform = useCallback(async () => {
    if (!audioUrl) return;
    
    const cacheKey = `${audioUrl}-${samples}`;
    
    // Check cache first
    if (waveformCache.has(cacheKey)) {
      const cached = waveformCache.get(cacheKey);
      setWaveformData(cached.waveform);
      setDuration(cached.duration);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      const fullUrl = getAssetUrl(audioUrl);
      
      // Fetch audio file
      const response = await fetch(fullUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Generate waveform
      const waveform = generateWaveformFromBuffer(audioBuffer, samples);
      const audioDuration = audioBuffer.duration * 1000; // Convert to ms
      
      // Cache the result
      waveformCache.set(cacheKey, { waveform, duration: audioDuration });
      
      setWaveformData(waveform);
      setDuration(audioDuration);
      
    } catch (err) {
      console.error('Waveform generation failed:', err);
      setError(err.message);
      
      // Generate fallback pseudo-random waveform
      const fallback = generateFallbackWaveform(audioUrl, samples);
      setWaveformData(fallback);
      
    } finally {
      setLoading(false);
    }
  }, [audioUrl, samples]);

  useEffect(() => {
    generateWaveform();
  }, [generateWaveform]);

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  return { waveformData, loading, error, duration };
}

/**
 * Generate fallback waveform when audio can't be loaded
 * Uses deterministic pseudo-random based on URL
 */
function generateFallbackWaveform(url, samples) {
  const waveform = [];
  let seed = url ? url.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 12345;
  
  for (let i = 0; i < samples; i++) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const random = (seed % 100) / 100;
    const baseHeight = 0.3 + random * 0.5;
    const variation = Math.sin(i * 0.3) * 0.15;
    waveform.push(Math.min(0.9, Math.max(0.1, baseHeight + variation)));
  }
  
  return waveform;
}

/**
 * Hook to manage waveforms for multiple audio clips
 */
export function useWaveformManager(audioAssets) {
  const [waveforms, setWaveforms] = useState({});
  const [loading, setLoading] = useState({});
  const audioContextRef = useRef(null);
  const loadingRef = useRef(new Set());

  const loadWaveform = useCallback(async (assetId, audioUrl, samples = 100) => {
    if (!audioUrl || loadingRef.current.has(assetId)) return;
    
    const cacheKey = `${audioUrl}-${samples}`;
    
    // Check cache
    if (waveformCache.has(cacheKey)) {
      const cached = waveformCache.get(cacheKey);
      setWaveforms(prev => ({ ...prev, [assetId]: cached }));
      return;
    }
    
    loadingRef.current.add(assetId);
    setLoading(prev => ({ ...prev, [assetId]: true }));
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const fullUrl = getAssetUrl(audioUrl);
      const response = await fetch(fullUrl);
      
      if (!response.ok) throw new Error('Failed to fetch');
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      const waveform = generateWaveformFromBuffer(audioBuffer, samples);
      const duration = audioBuffer.duration * 1000;
      
      const result = { waveform, duration };
      waveformCache.set(cacheKey, result);
      setWaveforms(prev => ({ ...prev, [assetId]: result }));
      
    } catch (err) {
      console.error(`Waveform load failed for ${assetId}:`, err);
      const fallback = { waveform: generateFallbackWaveform(audioUrl, samples), duration: 0 };
      setWaveforms(prev => ({ ...prev, [assetId]: fallback }));
      
    } finally {
      loadingRef.current.delete(assetId);
      setLoading(prev => ({ ...prev, [assetId]: false }));
    }
  }, []);

  // Auto-load waveforms for all assets
  useEffect(() => {
    if (!audioAssets?.length) return;
    
    for (const asset of audioAssets) {
      if (asset.file_url && !waveforms[asset.id]) {
        loadWaveform(asset.id, asset.file_url);
      }
    }
  }, [audioAssets, loadWaveform, waveforms]);

  const getWaveform = useCallback((assetId) => {
    return waveforms[assetId]?.waveform || null;
  }, [waveforms]);

  const getDuration = useCallback((assetId) => {
    return waveforms[assetId]?.duration || 0;
  }, [waveforms]);

  const isLoading = useCallback((assetId) => {
    return loading[assetId] || false;
  }, [loading]);

  return { waveforms, loadWaveform, getWaveform, getDuration, isLoading };
}

/**
 * Render waveform as SVG path
 */
export function WaveformPath({ data, width, height, className = '' }) {
  if (!data?.length) return null;
  
  const barWidth = width / data.length;
  
  return (
    <svg 
      width={width} 
      height={height} 
      className={className}
      preserveAspectRatio="none"
    >
      {data.map((value, i) => {
        const barHeight = value * height * 0.9;
        const x = i * barWidth;
        const y = (height - barHeight) / 2;
        
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={Math.max(barWidth - 1, 1)}
            height={barHeight}
            fill="currentColor"
            opacity={0.7}
          />
        );
      })}
    </svg>
  );
}
